// Global Typography System for HAV'N Interiors
// Ensures consistent font sizes and styles across all pages

export const TYPOGRAPHY = {
  // Font Family
  fontFamily: "'Helvetica Neue', 'Arial', sans-serif",
  
  // Headings
  h1: {
    fontSize: 'clamp(56px, 8vw, 96px)',
    fontWeight: 200,
    letterSpacing: '-3px',
    lineHeight: 1.1,
    color: 'var(--text-light)'
  },
  
  h2: {
    fontSize: 'clamp(40px, 5vw, 56px)',
    fontWeight: 300,
    letterSpacing: '-1.5px',
    lineHeight: 1.2,
    color: 'var(--text-light)'
  },
  
  h3: {
    fontSize: 'clamp(28px, 4vw, 36px)',
    fontWeight: 300,
    letterSpacing: '-0.5px',
    lineHeight: 1.3,
    color: 'var(--text-light)'
  },
  
  h4: {
    fontSize: 'clamp(20px, 3vw, 24px)',
    fontWeight: 400,
    letterSpacing: '0px',
    lineHeight: 1.4,
    color: 'var(--text-light)'
  },
  
  // Body Text
  body: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: 1.7,
    letterSpacing: '0.3px',
    color: 'rgba(245, 241, 237, 0.85)'
  },
  
  bodyLarge: {
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: 1.8,
    letterSpacing: '0.5px',
    color: 'rgba(245, 241, 237, 0.85)'
  },
  
  // Special Text
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: 'var(--accent-color)'
  },
  
  caption: {
    fontSize: '12px',
    fontWeight: 400,
    letterSpacing: '1px',
    color: 'rgba(245, 241, 237, 0.6)'
  }
};

// Local image URLs for different sections
// These images are downloaded from Unsplash (royalty-free) and stored locally
export const PLACEHOLDER_IMAGES = {
  // Hero/Banner images
  hero: `${process.env.PUBLIC_URL}/images/hero/hero-1.jpg`, // Modern luxury interior
  hero2: `${process.env.PUBLIC_URL}/images/hero/hero-2.jpg`, // Contemporary design
  hero3: `${process.env.PUBLIC_URL}/images/hero/hero-3.jpg`, // Elegant space
  hero4: `${process.env.PUBLIC_URL}/images/hero/hero-4.jpg`, // Minimalist interior
  hero5: `${process.env.PUBLIC_URL}/images/hero/hero-5.jpg`, // Classic design
  
  // Project category images
  residential: `${process.env.PUBLIC_URL}/images/projects/residential-living-1.jpg`, // Living room
  residentialBedroom: `${process.env.PUBLIC_URL}/images/projects/residential-bedroom-1.jpg`, // Bedroom
  residentialKitchen: `${process.env.PUBLIC_URL}/images/projects/residential-kitchen-1.jpg`, // Kitchen
  residentialDining: `${process.env.PUBLIC_URL}/images/projects/residential-dining-1.jpg`, // Dining room
  
  commercial: `${process.env.PUBLIC_URL}/images/projects/commercial-office-1.jpg`, // Office space
  commercialOffice2: `${process.env.PUBLIC_URL}/images/projects/commercial-office-2.jpg`, // Modern office
  commercialOffice3: `${process.env.PUBLIC_URL}/images/projects/commercial-office-3.jpg`, // Corporate office
  
  retail: `${process.env.PUBLIC_URL}/images/projects/commercial-retail-1.jpg`, // Retail store
  retail2: `${process.env.PUBLIC_URL}/images/projects/commercial-retail-2.jpg`, // Retail showroom
  
  restaurant: `${process.env.PUBLIC_URL}/images/projects/commercial-restaurant-1.jpg`, // Restaurant interior
  
  healthcare: `${process.env.PUBLIC_URL}/images/projects/institutional-healthcare-1.jpg`, // Healthcare facility
  education: `${process.env.PUBLIC_URL}/images/projects/institutional-education-1.jpg`, // Educational space
  
  industrial: `${process.env.PUBLIC_URL}/images/projects/industrial-warehouse-1.jpg`, // Industrial warehouse
  industrial2: `${process.env.PUBLIC_URL}/images/projects/industrial-facility-1.jpg`, // Industrial facility
  
  // Studio images - New names
  lumi: `${process.env.PUBLIC_URL}/images/studios/lumiceiling.jpg`, // LUMI CEILING (formerly Aether)
  lumi2: `${process.env.PUBLIC_URL}/images/studios/lumiceiling.jpg`,
  craftcove: `${process.env.PUBLIC_URL}/images/studios/craftcove.jpg`, // CRAFTCOVE (formerly Chisel)
  craftcove2: `${process.env.PUBLIC_URL}/images/studios/craftcove.jpg`,
  thread: `${process.env.PUBLIC_URL}/images/studios/thread.jpg`, // Thread (unchanged)
  thread2: `${process.env.PUBLIC_URL}/images/studios/thread.jpg`,
  artistry: `${process.env.PUBLIC_URL}/images/studios/artistryroom.jpg`, // ArtistryRoom (formerly Muse)
  artistry2: `${process.env.PUBLIC_URL}/images/studios/artistryroom.jpg`,
  globalnest: `${process.env.PUBLIC_URL}/images/studios/globalnest.jpg`, // Global Nest (formerly Nexus)
  globalnest2: `${process.env.PUBLIC_URL}/images/studios/globalnest.jpg`,
  surface: `${process.env.PUBLIC_URL}/images/studios/surfacestyle.jpg`, // Surfacestyle (formerly Strata)
  surface2: `${process.env.PUBLIC_URL}/images/studios/surfacestyle.jpg`,
  locknkey: `${process.env.PUBLIC_URL}/images/studios/locknkey.jpg`, // LockNKey (formerly Torque)
  locknkey2: `${process.env.PUBLIC_URL}/images/studios/locknkey.jpg`,
  shape: `${process.env.PUBLIC_URL}/images/studios/shapestudio.jpg`, // SHAPE STUDIO (formerly Carve)
  shape2: `${process.env.PUBLIC_URL}/images/studios/shapestudio.jpg`,
  
  // Legacy studio names (for backward compatibility)
  aether: `${process.env.PUBLIC_URL}/images/studios/lumiceiling.jpg`,
  aether2: `${process.env.PUBLIC_URL}/images/studios/lumiceiling.jpg`,
  chisel: `${process.env.PUBLIC_URL}/images/studios/craftcove.jpg`,
  chisel2: `${process.env.PUBLIC_URL}/images/studios/craftcove.jpg`,
  muse: `${process.env.PUBLIC_URL}/images/studios/artistryroom.jpg`,
  muse2: `${process.env.PUBLIC_URL}/images/studios/artistryroom.jpg`,
  nexus: `${process.env.PUBLIC_URL}/images/studios/globalnest.jpg`,
  nexus2: `${process.env.PUBLIC_URL}/images/studios/globalnest.jpg`,
  strata: `${process.env.PUBLIC_URL}/images/studios/surfacestyle.jpg`,
  strata2: `${process.env.PUBLIC_URL}/images/studios/surfacestyle.jpg`,
  torque: `${process.env.PUBLIC_URL}/images/studios/locknkey.jpg`,
  torque2: `${process.env.PUBLIC_URL}/images/studios/locknkey.jpg`,
  carve: `${process.env.PUBLIC_URL}/images/studios/shapestudio.jpg`,
  carve2: `${process.env.PUBLIC_URL}/images/studios/shapestudio.jpg`,
  thread: `${process.env.PUBLIC_URL}/images/studios/thread.jpg`, // Thread (unchanged)
  thread2: `${process.env.PUBLIC_URL}/images/studios/thread.jpg`,
  
  // Material textures
  woodTexture: `${process.env.PUBLIC_URL}/images/materials/wood-texture-1.jpg`,
  woodTexture2: `${process.env.PUBLIC_URL}/images/materials/wood-texture-2.jpg`,
  fabricTexture: `${process.env.PUBLIC_URL}/images/materials/fabric-texture-2.jpg`,
  marbleTexture: `${process.env.PUBLIC_URL}/images/materials/marble-texture-1.jpg`,
  marbleTexture2: `${process.env.PUBLIC_URL}/images/materials/marble-texture-2.jpg`,
  metalTexture: `${process.env.PUBLIC_URL}/images/materials/metal-texture-2.jpg`,
  
  // Team photos
  directorMale: `${process.env.PUBLIC_URL}/images/team/director-male-1.jpg`,
  directorFemale: `${process.env.PUBLIC_URL}/images/team/director-female-1.jpg`,
  teamMember1: `${process.env.PUBLIC_URL}/images/team/team-member-1.jpg`,
  teamMember2: `${process.env.PUBLIC_URL}/images/team/team-member-2.jpg`,
  teamMember3: `${process.env.PUBLIC_URL}/images/team/team-member-3.jpg`,
  teamMember4: `${process.env.PUBLIC_URL}/images/team/team-member-4.jpg`,
  
  // Process/Behind-the-scenes
  consultation: `${process.env.PUBLIC_URL}/images/process/consultation-1.jpg`,
  planning: `${process.env.PUBLIC_URL}/images/process/planning-1.jpg`,
  design: `${process.env.PUBLIC_URL}/images/process/design-work-1.jpg`,
  teamMeeting: `${process.env.PUBLIC_URL}/images/process/team-meeting-1.jpg`,
  
  // Café images
  cafe: `${process.env.PUBLIC_URL}/images/cafe/cafe-interior-1.jpg`,
  cafe2: `${process.env.PUBLIC_URL}/images/cafe/cafe-interior-2.jpg`,
  cafeWorkspace: `${process.env.PUBLIC_URL}/images/cafe/cafe-workspace-1.jpg`,
  cafeWorkspace2: `${process.env.PUBLIC_URL}/images/cafe/cafe-workspace-2.jpg`,
  
  // Legacy mappings for backward compatibility
  furniture: `${process.env.PUBLIC_URL}/images/studios/nexus-furniture-1.jpg`, // Global Nest
  lighting: `${process.env.PUBLIC_URL}/images/studios/muse-decor-1.jpg`, // ArtistryRoom
  flooring: `${process.env.PUBLIC_URL}/images/studios/strata-floor-1.jpg`, // Surfacestyle
  materials: `${process.env.PUBLIC_URL}/images/materials/marble-texture-1.jpg`,
  kitchen: `${process.env.PUBLIC_URL}/images/projects/residential-kitchen-1.jpg`,
  bedroom: `${process.env.PUBLIC_URL}/images/projects/residential-bedroom-1.jpg`,
  livingroom: `${process.env.PUBLIC_URL}/images/projects/residential-living-1.jpg`,
  office: `${process.env.PUBLIC_URL}/images/projects/commercial-office-1.jpg`,
  lobby: `${process.env.PUBLIC_URL}/images/hero/hero-2.jpg`,
  team: `${process.env.PUBLIC_URL}/images/process/team-meeting-1.jpg`,
  hospitality: `${process.env.PUBLIC_URL}/images/projects/commercial-restaurant-1.jpg`,
  wallcovering: `${process.env.PUBLIC_URL}/images/materials/fabric-texture-2.jpg`,
  bathroom: `${process.env.PUBLIC_URL}/images/projects/residential-living-2.jpg`
};

